import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  knowledgeDocInstanceMatchesOcrId,
  ocrResultMatchKey,
} from 'features/knowledgeDocumentViewer/utils/viewerOcrBoxId'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { KnowledgeDocumentEntry, RolodexEntryType } from 'types'

import { OcrResultWithPage } from './useOcrResults'

export const useOcrRefsCount = (ocrResults: OcrResultWithPage[]) => {
  const { currentDocumentId } = useKnowledgeDocumentUXContext()
  const { siteID } = useKnowledgeDocumentDataContext()
  const docTabData = useDocumentTabDataContextOptional()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const knowledgeDocEntries = useMemo(
    () => docTabData?.knowledgeDocumentEntriesForSelectedDocument ?? [],
    [docTabData?.knowledgeDocumentEntriesForSelectedDocument]
  )
  const isLoadingKnowledgeDocuments =
    docTabData?.isLoadingKnowledgeDocuments ?? false

  const ocrResultUnitMap = useMemo(() => {
    const map = new Map<string, string | undefined>()

    ocrResults.forEach((result) => {
      const key = ocrResultMatchKey(result.id, result.pageNumber)

      const knowledgeDoc = knowledgeDocEntries.find(
        (document: KnowledgeDocumentEntry) =>
          knowledgeDocInstanceMatchesOcrId(
            document.entry.data.fields.instance_id,
            result.id
          ) &&
          Number(document.entry.data.fields.page_number) === result.pageNumber
      )

      map.set(key, knowledgeDoc?.entry.tags.unit)
    })

    return map
  }, [ocrResults, knowledgeDocEntries])

  const uniqueUnitTags = useMemo(() => {
    const units = new Set<string>()
    ocrResultUnitMap.forEach((unit) => {
      if (unit) units.add(unit)
    })
    return Array.from(units)
  }, [ocrResultUnitMap])

  const hasUnitTags = uniqueUnitTags.length > 0

  const relevantKnowledgeDocumentLoadOptions = useMemo(
    () =>
      isPipelineConfigScopeLoading || !hasUnitTags
        ? undefined
        : {
            type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
            tags: getPipelineConfigScopedTags({
              site: [siteID],
              unit: uniqueUnitTags,
            }),
            limit: 1000000,
          },
    [
      getPipelineConfigScopedTags,
      hasUnitTags,
      isPipelineConfigScopeLoading,
      siteID,
      uniqueUnitTags,
    ]
  )
  const { data: relevantKnowledgeDocuments, isLoading: isLoadingRelevantDocs } =
    useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>(
      relevantKnowledgeDocumentLoadOptions
    )

  const isLoadingRefs =
    isLoadingKnowledgeDocuments || (hasUnitTags && isLoadingRelevantDocs)

  const unitRefsCountMap = useMemo(() => {
    const map = new Map<string, number>()

    uniqueUnitTags.forEach((unit) => {
      const linkedDocs =
        relevantKnowledgeDocuments?.filter(
          (entry) =>
            entry.entry.tags.unit === unit &&
            entry.entry.tags['system-document'] !== currentDocumentId
        ) ?? []
      map.set(unit, linkedDocs.length)
    })

    return map
  }, [uniqueUnitTags, relevantKnowledgeDocuments, currentDocumentId])

  const ocrResultRefsMap = useMemo(() => {
    const map = new Map<string, number>()

    ocrResults.forEach((result) => {
      const key = ocrResultMatchKey(result.id, result.pageNumber)
      const unitTag = ocrResultUnitMap.get(key)
      const refsCount = unitTag ? (unitRefsCountMap.get(unitTag) ?? 0) : 0
      map.set(key, refsCount)
    })

    return map
  }, [ocrResults, ocrResultUnitMap, unitRefsCountMap])

  const getLinkedDocumentsForResult = useCallback(
    (result: OcrResultWithPage) => {
      const key = ocrResultMatchKey(result.id, result.pageNumber)
      const unitTag = ocrResultUnitMap.get(key)

      if (!unitTag) return []

      return (
        relevantKnowledgeDocuments?.filter(
          (entry) =>
            entry.entry.tags.unit === unitTag &&
            entry.entry.tags['system-document'] !== currentDocumentId
        ) ?? []
      )
    },
    [ocrResultUnitMap, relevantKnowledgeDocuments, currentDocumentId]
  )

  return {
    ocrResultRefsMap,
    ocrResultUnitMap,
    getLinkedDocumentsForResult,
    isLoadingRefs,
  }
}
